import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Dialog, ErrorIcon, Heading, Icon, Pane, Spinner, Text, TickCircleIcon } from 'evergreen-ui';

import Request from '../../../util/Api';

const PRINT_AGENT_URL = 'http://127.0.0.1:9876';

// Keystrokes arriving faster than this are a hardware scanner, not a human typing.
const SCAN_CHAR_GAP_MS = 60;

// Sentinel + order number — mirror of the printout barcode payload built by
// buildScanCommand in api/controllers/order/services/order-printout.service.js.
// Keep SCAN_SENTINEL in lockstep with the SCAN_SENTINEL constant there.
const SCAN_SENTINEL = '~';
const COMMAND_RE = /^~(.+)$/;

// Past "processed" the scan is a no-op — operator gets the red modal. 'processed' itself is
// handled separately (offer an AWB reprint) before this check.
const BLOCKED_STATUSES = ['delivered', 'completed', 'returning', 'returned', 'cancelled'];

// Reprint-safe: hits the local Print Agent to (re)print only the AWB label (A6). Throws if
// the agent is unreachable; resolves { ok, awbErr } otherwise. Never re-registers a shipment.
const printOrderAwb = async (orderId) => {
    const r = await fetch(`${PRINT_AGENT_URL}/print/order/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ print_printout: false, print_awb: true, awb_format: 'A6' }),
    });
    const data = await r.json().catch(() => ({}));
    const ok = !!(r.ok && data && data.success);
    const awbErr = (data && data.results && data.results.awb && data.results.awb.error) || 'raspuns invalid de la Print Agent';
    return { ok, awbErr };
};

const STATUS_RO = {
    placed: 'Plasata',
    confirmed: 'Confirmata',
    processed: 'Procesata',
    delivered: 'Livrata',
    completed: 'Finalizata',
    cancelled: 'Anulata',
    returning: 'Retur in curs',
    returned: 'Returnata',
};

const ERROR_RO = {
    ERR_ORDER_NEEDS_PAYMENT: 'Plata comenzii nu a fost confirmata (card/transfer). Confirma plata inainte de procesare.',
    ERR_NOT_ENOUGH_IN_STOCK: 'Stoc insuficient pentru a procesa comanda.',
    ERR_ORDER_DOES_NOT_EXIST: 'Comanda nu exista.',
    ERR_NO_STATUS: 'Status lipsa.',
};

const mapError = (e) => {
    const code = typeof e === 'string' ? e : (e && e.message) || 'UNKNOWN';
    return ERROR_RO[code] || `Eroare la procesare: ${code}`;
};

// Mirrors the manual "Procesare comanda" modal defaults (OrderStatusProcessedModal).
// Scan dispatch always uses Sameday; international is forced to DPD backend-side
// regardless — see awb-payload.builder buildBaseAwbPayload.
const defaultProcessExtraData = (order) => {
    const isDelivery = ['delivery', 'delivery-locker'].includes(order.delivery);
    return {
        packed: true,
        stocked: true,
        emailNotifications: true,
        smsNotifications: true,
        packages: isDelivery ? [{ width: 10, height: 10, length: 10 }] : [],
        personalDropoff: false,
        provider: 'sameday',
        pickupPoint: order.locationId === 2 ? 'BRASOV' : 'PLOIESTI',
    };
};

const PHASE_TITLE = {
    confirm: 'Comanda deja procesata',
    working: 'Procesare comanda...',
    success: 'Comanda procesata',
    warning: 'Comanda procesata — atentie',
    error: 'Eroare procesare comanda',
};

const BANNER = {
    success: { bg: '#F5FBF8', border: '#429777', color: '#317159' },
    warning: { bg: '#FDF8F3', border: '#996A13', color: '#85640E' },
    confirm: { bg: '#FDF8F3', border: '#996A13', color: '#85640E' },
    error: { bg: '#FDF4F4', border: '#D14343', color: '#A73636' },
};

function StepRow({ step }) {
    const icon = step.status === 'done'
        ? <Icon icon={TickCircleIcon} color="success" marginRight={8} />
        : step.status === 'error'
            ? <Icon icon={ErrorIcon} color="danger" marginRight={8} />
            : <Spinner size={16} marginRight={8} />;
    return (
        <Pane display="flex" alignItems="center" marginBottom={6}>
            {icon}
            <Text color={step.status === 'error' ? 'danger' : 'default'}>{step.label}</Text>
        </Pane>
    );
}

export default function OrderScanCommand() {
    const user = useSelector((s) => s.authentication && s.authentication.user);
    const enabled = !!(user && user.role && user.role.level >= 2);

    const [phase, setPhase] = useState('idle'); // idle | working | confirm | success | warning | error
    const [orderNumber, setOrderNumber] = useState('');
    const [steps, setSteps] = useState([]);
    const [message, setMessage] = useState('');

    const runningRef = useRef(false);
    const bufRef = useRef('');
    const lastTsRef = useRef(0);
    const runRef = useRef(null);
    const pendingOrderRef = useRef(null);

    const setLastStep = (status) =>
        setSteps((prev) => prev.map((s, i) => (i === prev.length - 1 ? { ...s, status } : s)));

    const runCommand = useCallback(async (scannedNumber) => {
        if (runningRef.current) return;
        runningRef.current = true;

        setOrderNumber(scannedNumber);
        setMessage('');
        setSteps([{ label: `Cautare comanda #${scannedNumber}`, status: 'active' }]);
        setPhase('working');

        const addStep = (label) => setSteps((prev) => [...prev, { label, status: 'active' }]);
        const fail = (msg) => {
            setLastStep('error');
            setMessage(msg);
            setPhase('error');
        };

        try {
            const res = await Request.get('/orders', {}, { q: scannedNumber, _start: 0, _end: 10, _sort: 'id', _order: 'DESC' });
            const order = ((res && res.data) || []).find((o) => String(o.orderNumber) === String(scannedNumber));
            if (!order) {
                fail(`Comanda #${scannedNumber} nu a fost gasita.`);
                return;
            }
            setLastStep('done');

            if (order.status === 'processed') {
                pendingOrderRef.current = order;
                setSteps([]);
                setMessage(`Comanda #${scannedNumber} este deja procesata si are AWB generat. Reprintezi eticheta de transport? Atentie: nu expedia coletul de doua ori — reprintarea doar tipareste din nou eticheta existenta, nu genereaza un transport nou.`);
                setPhase('confirm');
                return;
            }

            if (BLOCKED_STATUSES.includes(order.status)) {
                setMessage(`Comanda #${scannedNumber} este deja in stadiul "${STATUS_RO[order.status] || order.status}" si nu mai poate fi procesata.`);
                setPhase('error');
                return;
            }

            if (order.status === 'placed') {
                addStep('Confirmare comanda');
                try {
                    await Request.put('/orders/[id]/status-update', { id: order.id }, null, { status: 'confirmed' });
                    setLastStep('done');
                } catch (e) {
                    fail(mapError(e));
                    return;
                }
            }

            addStep('Procesare comanda si generare AWB');
            try {
                await Request.put('/orders/[id]/status-update', { id: order.id }, null, {
                    status: 'processed',
                    extraData: defaultProcessExtraData(order),
                });
                setLastStep('done');
            } catch (e) {
                fail(mapError(e));
                return;
            }

            addStep('Printare AWB (A6)');
            try {
                const { ok, awbErr } = await printOrderAwb(order.id);
                if (ok) {
                    setLastStep('done');
                    setMessage(`Comanda #${scannedNumber} a fost procesata si AWB-ul a fost trimis la imprimanta.`);
                    setPhase('success');
                } else {
                    setLastStep('error');
                    setMessage(`Comanda #${scannedNumber} a fost procesata, dar printarea AWB a esuat (${awbErr}). Printeaza AWB-ul manual din pagina comenzii.`);
                    setPhase('warning');
                }
            } catch (e) {
                setLastStep('error');
                setMessage(`Comanda #${scannedNumber} a fost procesata, dar Print Agent nu este disponibil. Porneste aplicatia Print Agent si printeaza AWB-ul (A6) manual.`);
                setPhase('warning');
            }
        } catch (e) {
            fail(mapError(e));
        } finally {
            runningRef.current = false;
        }
    }, []);

    runRef.current = runCommand;

    const reprintAwb = useCallback(async () => {
        const order = pendingOrderRef.current;
        if (!order || runningRef.current) return;
        runningRef.current = true;

        const scannedNumber = order.orderNumber;
        setMessage('');
        setSteps([{ label: 'Reprintare AWB (A6)', status: 'active' }]);
        setPhase('working');

        try {
            const { ok, awbErr } = await printOrderAwb(order.id);
            if (ok) {
                setLastStep('done');
                setMessage(`AWB-ul comenzii #${scannedNumber} a fost retrimis la imprimanta. Atentie: nu expedia coletul de doua ori.`);
                setPhase('success');
            } else {
                setLastStep('error');
                setMessage(`Reprintarea AWB pentru comanda #${scannedNumber} a esuat (${awbErr}). Printeaza AWB-ul manual din pagina comenzii.`);
                setPhase('warning');
            }
        } catch (e) {
            setLastStep('error');
            setMessage('Print Agent nu este disponibil. Porneste aplicatia Print Agent si printeaza AWB-ul (A6) manual.');
            setPhase('warning');
        } finally {
            runningRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;

        const onKeyDown = (e) => {
            const now = Date.now();
            if (now - lastTsRef.current > SCAN_CHAR_GAP_MS) bufRef.current = '';
            lastTsRef.current = now;

            if (e.key === 'Enter') {
                const buf = bufRef.current;
                bufRef.current = '';
                if (buf.startsWith(SCAN_SENTINEL)) {
                    e.preventDefault();
                    e.stopPropagation();
                    const m = buf.match(COMMAND_RE);
                    if (m && runRef.current) runRef.current(m[1]);
                }
                return;
            }

            if (e.key && e.key.length === 1) {
                bufRef.current += e.key;
                // Swallow keystrokes only once the sentinel is followed by >=1 char (a real
                // scan burst), so a human typing a lone '~' into an input isn't eaten.
                if (bufRef.current.length > 1 && bufRef.current.startsWith(SCAN_SENTINEL)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [enabled]);

    // Auto-dismiss the success modal; keep warning/error until the operator closes them.
    useEffect(() => {
        if (phase !== 'success') return undefined;
        const t = setTimeout(() => setPhase('idle'), 6000);
        return () => clearTimeout(t);
    }, [phase]);

    if (phase === 'idle') return null;

    const closable = phase !== 'working';
    const banner = BANNER[phase];

    return (
        <Dialog
            isShown
            title={PHASE_TITLE[phase]}
            hasFooter={false}
            hasClose={closable}
            shouldCloseOnOverlayClick={closable}
            shouldCloseOnEscapePress={closable}
            onCloseComplete={() => setPhase('idle')}
            preventBodyScrolling
        >
            <Pane>
                <Heading size={500} marginBottom={12}>Comanda #{orderNumber}</Heading>

                <Pane marginBottom={message ? 16 : 0}>
                    {steps.map((step, i) => <StepRow key={i} step={step} />)}
                </Pane>

                {message ? (
                    <Pane
                        padding={12}
                        borderRadius={4}
                        background={banner ? banner.bg : '#F4F5F9'}
                        border={`1px solid ${banner ? banner.border : '#D8DAE5'}`}
                    >
                        <Text color={banner ? banner.color : 'default'}>{message}</Text>
                    </Pane>
                ) : null}

                {phase === 'confirm' ? (
                    <Pane display="flex" justifyContent="flex-end" marginTop={16}>
                        <Button onClick={() => setPhase('idle')} marginRight={12}>Anuleaza</Button>
                        <Button appearance="primary" intent="warning" onClick={reprintAwb}>Reprinteaza AWB</Button>
                    </Pane>
                ) : null}
            </Pane>
        </Dialog>
    );
}
